import { useCallback, useMemo } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Link,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import {
  AccountCircle,
  CheckCircle,
  FileDownloadOutlined,
} from "@mui/icons-material";

import __ from "src/utils/Translations";
import {
  getConditionLabel,
  PendingConditionTypes,
  SELECTED_ROW_CLASS,
  SignedDocumentConditionType,
} from "src/Pages/Company/Pending/PendingBenefits/const";
import {
  IWaitingRoom,
  IWaitingRoomCondition,
} from "src/Pages/Company/Pending/PendingBenefits/types";
import { useSelectedRows } from "src/Pages/Company/Pending/PendingBenefits/SelectedRowsProvider/hooks";
import { DeclineWaitingRoom } from "./DeclineWaitingRoom";
import { AcceptWaitingRoomCondition } from "./AcceptWaitingRoomCondition";
import { CellWithDividerStyle, ConditionSeparatorStyle } from "./TableStyles";

export const WaitingRoomRow = ({
  waitingRoom,
  conditions,
}: {
  waitingRoom: IWaitingRoom;
  conditions: IWaitingRoomCondition[];
}) => {
  const { isWaitingRoomSelected, selectWaitingRoom } = useSelectedRows();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      selectWaitingRoom(waitingRoom)(event.target.checked),
    [selectWaitingRoom, waitingRoom]
  );

  const { acceptedConditions, pendingConditions, isWaitingRoomSelectable } =
    useMemo(() => {
      const pendingConditions = conditions.filter(
        ({ accepted, conditionAcceptable }) => conditionAcceptable && !accepted
      );
      return {
        acceptedConditions: conditions.filter(
          ({ accepted, conditionAcceptable }) =>
            (conditionAcceptable && accepted) || !conditionAcceptable
        ),
        pendingConditions,
        isWaitingRoomSelectable:
          waitingRoom.cancellable || pendingConditions.length > 0,
      };
    }, [conditions, waitingRoom.cancellable]);

  return (
    <TableRow
      className={isWaitingRoomSelected(waitingRoom) ? SELECTED_ROW_CLASS : ""}
    >
      <TableCell padding="checkbox" sx={{ paddingLeft: "24px" }}>
        {isWaitingRoomSelectable && (
          <Checkbox
            color="primary"
            checked={isWaitingRoomSelected(waitingRoom)}
            onChange={handleChange}
            aria-label={__("Zaznacz")}
          />
        )}
      </TableCell>
      <TableCell sx={CellWithDividerStyle}>
        <Typography variant="body1">{waitingRoom.name}</Typography>
        {waitingRoom.beneficiaryFirstName !== "-" &&
          waitingRoom.beneficiaryLastName !== "-" && (
            <Typography variant="body2">
              {waitingRoom.beneficiaryFirstName}{" "}
              {waitingRoom.beneficiaryLastName || "..."}
            </Typography>
          )}
      </TableCell>
      <TableCell sx={CellWithDividerStyle}>
        {waitingRoom.createdAt && (
          <Typography variant="body2">
            {new Date(waitingRoom.createdAt).toLocaleDateString("pl-PL", {
              timeZone: "Europe/Warsaw",
            })}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right" sx={CellWithDividerStyle}>
        {waitingRoom.totalCost && (
          <Typography variant="body2">{waitingRoom.totalCost} zł</Typography>
        )}
      </TableCell>
      <TableCell align="right" sx={CellWithDividerStyle}>
        {waitingRoom.paidByEmployee && (
          <Typography variant="body2">
            {waitingRoom.paidByEmployee} zł
          </Typography>
        )}
      </TableCell>
      <TableCell sx={CellWithDividerStyle}>
        <Stack sx={{ gap: 1.5, alignItems: "start" }}>
          {acceptedConditions.map((condition) => (
            <ConditionStatus
              key={condition.id}
              condition={condition}
              waitingRoom={waitingRoom}
            />
          ))}
        </Stack>
      </TableCell>
      <TableCell sx={{ paddingRight: "24px" }}>
        <Stack sx={{ gap: 1 }}>
          {pendingConditions.map((condition) => (
            <Stack
              key={condition.id}
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "nowrap",
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle2">
                  {__(getConditionLabel(condition.type, condition.accepted))}
                </Typography>

                {condition.type === SignedDocumentConditionType &&
                  waitingRoom.documentUrl && (
                    <Box>
                      <Link
                        href={waitingRoom.documentUrl}
                        color="textPrimary"
                        variant="body1"
                        download
                        noWrap
                        aria-label={__("Pobierz podpisany dokument")}
                      >
                        {waitingRoom.barcodeId ||
                          __("Pobierz podpisany dokument")}
                        <FileDownloadOutlined aria-hidden="true" />
                      </Link>
                    </Box>
                  )}

                {condition.documentUrl && (
                  <Box>
                    <Link
                      href={condition.documentUrl}
                      download
                      color="textPrimary"
                      variant="body1"
                      noWrap
                      aria-label={__("Pobierz dokument")}
                    >
                      {condition.barcodeId || __("Pobierz dokument")}
                      <FileDownloadOutlined aria-hidden="true" />
                    </Link>
                  </Box>
                )}
              </Box>
              <AcceptWaitingRoomCondition
                waitingRoom={waitingRoom}
                condition={condition}
              />
            </Stack>
          ))}
          {waitingRoom.cancellable && (
            <Stack
              direction="row"
              sx={[
                { justifyContent: "end" },
                pendingConditions.length > 0 ? ConditionSeparatorStyle : {},
              ]}
            >
              <DeclineWaitingRoom waitingRoom={waitingRoom} />
            </Stack>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const ConditionStatus = ({
  condition,
  waitingRoom,
}: {
  condition: IWaitingRoomCondition;
  waitingRoom: IWaitingRoom;
}) => (
  <Chip
    icon={ChipIcon({ condition })}
    label={ChipLabel({ condition, waitingRoom })}
    color={condition.accepted ? "success" : "default"}
    size="small"
    variant="outlined"
    sx={{
      bgcolor: condition.accepted ? "green.50" : "white",
      letterSpacing: "0.16px",
      lineHeight: "18px",
    }}
  />
);

const ChipIcon = ({ condition }: { condition: IWaitingRoomCondition }) => {
  if (condition.accepted) {
    return <CheckCircle aria-hidden="true" />;
  }
  if (PendingConditionTypes.includes(condition.type)) {
    return <AccountCircle aria-hidden="true" />;
  }
  return undefined;
};

const ChipLabel = ({
  condition,
  waitingRoom,
}: {
  condition: IWaitingRoomCondition;
  waitingRoom: IWaitingRoom;
}) => {
  if (
    condition.type === SignedDocumentConditionType &&
    waitingRoom.documentUrl
  ) {
    return (
      <Link
        href={waitingRoom.documentUrl}
        download
        color="textPrimary"
        aria-label={__("Pobierz podpisany dokument")}
      >
        {__(getConditionLabel(condition.type, condition.accepted))}
        <FileDownloadOutlined
          color="success"
          fontSize="small"
          aria-hidden="true"
          sx={{ fontSize: "18px" }}
        />
      </Link>
    );
  }
  if (condition.documentUrl) {
    return (
      <Link
        href={condition.documentUrl}
        download
        color="textPrimary"
        aria-label={__("Pobierz dokument")}
      >
        {condition.barcodeId || __("Pobierz dokument")}
        <FileDownloadOutlined
          color="success"
          fontSize="small"
          aria-hidden="true"
          sx={{ fontSize: "18px" }}
        />
      </Link>
    );
  }
  return __(getConditionLabel(condition.type, condition.accepted));
};
